// frontend/src/components/SupplierDecodedSidebar.js  (replacement)
// Chrome/ink sidebar matching WorkshopSidebar: grouped nav, stroke icons
// instead of emoji, the 1Parc mark, and French throughout. Was slate/orange
// with English labels, which made the supplier space look like a different
// product from the owner and workshop ones.

import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import IparcMark from './IparcMark';
import {
  IconHome, IconClipboard, IconCheck, IconCard, IconBox, IconDoc,
  IconHelp, IconLogout,
} from './icons/Icons';

// A till slip with a torn bottom edge — reads as "reçu" rather than "facture",
// which already owns IconCard here.
const IconReceipt = ({ className = 'w-[17px] h-[17px]' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3h12v18l-2-1.5L14 21l-2-1.5L10 21l-2-1.5L6 21z" />
    <path d="M9 8h6M9 12h6" />
  </svg>
);
const IconUser = ({ className = 'w-[17px] h-[17px]' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round">
    <circle cx="12" cy="8.5" r="3.5" /><path d="M5 20a7 7 0 0 1 14 0" />
  </svg>
);

export default function SupplierDecodedSidebar({
  className = '', onClose, availableRfqs = 0, unpaidInvoices = 0, pendingCounters = 0, onLogout,
}) {
  const { user } = useAuth();

  const sections = [
    {
      title: 'Activité',
      items: [
        { label: 'Tableau de bord', icon: IconHome, path: '/supplier' },
        // Where a buyer's counter-offer is answered. Badged because a
        // negotiation waiting on you is the one thing here that costs money
        // to ignore.
        { label: 'Mes devis', icon: IconDoc, path: '/supplier/quotes', badge: pendingCounters, badgeColor: '#DFA23C' },
        // Was "Respond to Quotation" pointing at /respond-quotation/0 — a
        // hard-coded id that resolves to nothing. Responding starts from a
        // real request on the dashboard.
        { label: 'Devis acceptés', icon: IconCheck, path: '/supplier/accepted' },
      ],
    },
    {
      title: 'Documents',
      items: [
        { label: 'Mes factures', icon: IconCard, path: '/supplier/invoices', badge: unpaidInvoices, badgeColor: '#E2574B' },
        { label: 'Reçus de paiement', icon: IconReceipt, path: '/supplier/receipts' },
        { label: 'Bons de commande', icon: IconBox, path: '/supplier/purchase-orders' },
      ],
    },
    {
      title: 'Compte',
      items: [
        { label: 'Profil fournisseur', icon: IconUser, path: '/supplier/profile' },
      ],
    },
  ];

  const linkClass = ({ isActive }) =>
    `relative flex items-center gap-[11px] px-2.5 py-[9px] rounded-[9px] text-[13px] transition-colors ${
      isActive
        ? 'bg-[#1E1B17] text-[#F4F1EA] font-medium'
        : 'text-[#9A9184] hover:bg-[#171512] hover:text-[#F4F1EA] font-normal'
    }`;

  return (
    <aside
      className={`w-[248px] h-full bg-[#100F0D] border-r border-[#211E1B] px-4 pt-[22px] pb-[18px]
                  flex flex-col gap-[22px] overflow-y-auto ${className}`}
      onClick={onClose}
    >
      <div className="flex items-center gap-[11px] px-1.5">
        <IparcMark size={38} />
        <div className="leading-[1.1]">
          <p className="font-['Space_Grotesk',sans-serif] text-sm font-semibold tracking-[.14em] text-[#F4F1EA]">IPARC</p>
          <p className="text-[9.5px] uppercase tracking-[.16em] text-[#6E665C] mt-0.5">Espace fournisseur</p>
        </div>
      </div>

      <div className="px-3 py-[11px] border border-[#241F1B] rounded-xl bg-gradient-to-b from-[#17140F] to-[#131110]">
        <p className="text-[9px] uppercase tracking-[.18em] text-[#6E665C] mb-[5px]">Fournisseur</p>
        <p className="font-['Space_Grotesk',sans-serif] text-[13.5px] font-semibold tracking-[-.01em] text-[#F4F1EA] truncate">
          {user?.businessName || user?.name || 'Fournisseur'}
        </p>
        {(user?.city || user?.address) && (
          <p className="text-[11px] text-[#8F877B] mt-[3px] truncate">{user.city || user.address}</p>
        )}
      </div>

      <nav className="flex flex-col gap-[18px]">
        {sections.map((section) => (
          <div key={section.title} className="flex flex-col gap-0.5">
            <p className="text-[9px] uppercase tracking-[.2em] text-[#5C554C] px-2.5 pb-[7px]">{section.title}</p>
            {section.items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink key={item.path} to={item.path} end={item.path === '/supplier'} className={linkClass}>
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span className="absolute left-0 top-[9px] bottom-[9px] w-0.5 rounded-sm bg-gradient-to-b from-[#F4F1EA] to-[#8A8780]" />
                      )}
                      <Icon className="w-[17px] h-[17px] shrink-0" />
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="text-[10px] font-bold text-[#0D0C0B] rounded-full px-1.5 tabular-nums"
                              style={{ background: item.badgeColor }}>
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="mt-auto pt-3.5 border-t border-[#211E1B] flex flex-col gap-0.5">
        <NavLink to="/support"
          className="flex items-center gap-[11px] px-2.5 py-2 rounded-[9px] text-[12.5px] text-[#7C7468] hover:bg-[#171512] hover:text-[#F4F1EA] transition-colors">
          <IconHelp className="w-4 h-4" /> Aide &amp; support
        </NavLink>
        {onLogout && (
          <button onClick={onLogout}
            className="flex items-center gap-[11px] px-2.5 py-2 rounded-[9px] text-[12.5px] text-[#7C7468] hover:bg-[#1E1512] hover:text-[#E2574B] transition-colors text-left">
            <IconLogout className="w-4 h-4" /> Déconnexion
          </button>
        )}
      </div>
    </aside>
  );
}
